import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams as useReactRouterParams } from "react-router-dom";
import { Heart, MessageCircle, Share2, Video, Mic, X, Camera, Send, Sparkles, Play, Music2, RotateCcw, Radio, ImagePlus } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import MusicSheet from "../../components/social/MusicSheet";

/* ═══════════════════════════════════════════════════════════════════
   Tap IN Social — combined Feed page (Stories + Posts).
   The Reels/Live/Profile pages are siblings of this file but share a
   simple visual language so the whole experience feels consistent.
   ═══════════════════════════════════════════════════════════════════ */

export function SocialFeedV1() {
  const { user } = useAuth();
  const [stories, setStories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/community/stories?limit=24").then((r) => r.data?.stories || []).catch(() => []),
      api.get("/community/feed?limit=40").then((r) => r.data?.posts || r.data?.items || []).catch(() => []),
    ]).then(([s, p]) => { setStories(s); setPosts(p); setLoading(false); });
  }, []);

  return (
    <div data-testid="social-feed" className="space-y-3 sm:space-y-4">
      {/* Stories rail (Instagram-style) */}
      <section data-testid="social-stories" className="px-3 sm:px-0">
        <div className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar">
          <Link to="/social/create?type=story" data-testid="story-add" className="shrink-0 w-16 sm:w-20 flex flex-col items-center text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-pink-500 via-fuchsia-500 to-amber-400 p-[3px]">
              <div className="w-full h-full rounded-full bg-[#0c0915] flex items-center justify-center text-2xl">+</div>
            </div>
            <div className="text-[10px] mt-1 truncate">Your story</div>
          </Link>
          {stories.map((s) => (
            <Link key={s.id} to={`/social/story/${s.id}`} data-testid={`story-${s.id}`} className="shrink-0 w-16 sm:w-20 flex flex-col items-center text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-pink-500 via-fuchsia-500 to-amber-400 p-[3px]">
                <img src={s.media_url || s.thumbnail_url || s.user?.picture} alt="" className="w-full h-full object-cover rounded-full ring-2 ring-[#0c0915]" />
              </div>
              <div className="text-[10px] mt-1 truncate w-full">{s.user?.handle || s.user?.name}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Posts feed */}
      {loading && <div className="text-center text-white/50 py-12">Loading…</div>}
      {!loading && posts.length === 0 && (
        <div data-testid="social-feed-empty" className="bg-black/30 border border-dashed border-white/10 rounded-2xl mx-3 sm:mx-0 p-8 text-center">
          <Sparkles className="w-8 h-8 text-pink-400 mx-auto mb-2" />
          <div className="font-bold">It's quiet here…</div>
          <div className="text-white/60 text-sm mt-1">Be the first to post — tap the + button.</div>
        </div>
      )}
      <div className="space-y-3 sm:space-y-4">
        {posts.map((p) => <PostCard key={p.id} post={p} />)}
      </div>
    </div>
  );
}

function PostCard({ post }) {
  const [liked, setLiked] = useState(post.liked_by_me);
  const [count, setCount] = useState(post.like_count || 0);
  const isVideo = post.video_url || (post.media_type === "video");
  return (
    <article data-testid={`social-post-${post.id}`} className="bg-black/40 sm:rounded-2xl border-y sm:border border-white/10 overflow-hidden">
      {/* Author row */}
      <header className="flex items-center gap-2 p-3">
        <Link to={`/social/u/${post.user_id || post.author_id}`} className="flex items-center gap-2 min-w-0 flex-1">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-500 to-fuchsia-500 ring-2 ring-white/15 overflow-hidden">
            {post.user?.picture
              ? <img src={post.user.picture} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center text-sm font-black">{(post.user?.name || "?")[0]}</div>}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold truncate">{post.user?.name || post.author_name || "Someone"}</div>
            <div className="text-[10px] text-white/50 truncate">@{post.user?.handle || post.handle || "user"}</div>
          </div>
        </Link>
        {post.category && (
          <span className="bg-pink-500/20 text-pink-300 text-[10px] uppercase tracking-wider font-black px-2 py-0.5 rounded-full">{post.category}</span>
        )}
      </header>
      {/* Media */}
      {(post.media_url || post.video_url || post.thumbnail_url) && (
        <div className="bg-black aspect-square sm:aspect-auto sm:max-h-[640px] flex items-center justify-center">
          {isVideo
            ? <video src={post.video_url || post.media_url} controls playsInline className="w-full h-full object-contain" />
            : <img src={post.media_url || post.thumbnail_url} alt="" className="w-full h-full object-cover" />}
        </div>
      )}
      {/* Actions + caption */}
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-4">
          <button
            data-testid={`post-like-${post.id}`}
            onClick={async () => {
              setLiked((l) => !l); setCount((n) => n + (liked ? -1 : 1));
              try { await api.post(`/community/posts/${post.id}/like`); } catch {}
            }}
            className="inline-flex items-center gap-1.5 hover:text-pink-400"
          >
            <Heart className={`w-6 h-6 ${liked ? "fill-pink-500 text-pink-500" : ""}`} />
            <span className="text-sm font-bold">{count}</span>
          </button>
          <Link to={`/social/p/${post.id}`} className="inline-flex items-center gap-1.5 hover:text-fuchsia-400">
            <MessageCircle className="w-6 h-6" />
            <span className="text-sm font-bold">{post.comment_count || 0}</span>
          </Link>
          <button
            onClick={async () => {
              try { await navigator.share?.({ title: post.title || "Tap IN Social", url: `${window.location.origin}/social/p/${post.id}` }); }
              catch { try { await navigator.clipboard.writeText(`${window.location.origin}/social/p/${post.id}`); toast.success("Link copied"); } catch {} }
            }}
            className="inline-flex items-center gap-1.5 hover:text-emerald-400 ml-auto"
          ><Share2 className="w-6 h-6" /></button>
        </div>
        {(post.title || post.caption) && <div className="text-sm leading-snug whitespace-pre-wrap">{post.title || post.caption}</div>}
      </div>
    </article>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Create flow — TikTok / Instagram-style camera-first creator.
   • Live camera preview the moment the page mounts (with fallback if
     the user denies access).
   • Mode tabs: PHOTO · VIDEO · STORY · LIVE
   • Hold-to-record (max 60s for video, 15s for story).
   • Music picker bottom sheet — search Tap IN's curated library and
     attach a 30-sec preview track to your post.
   • Flip camera, gallery picker, and the prominent GO LIVE button
     that hands off to /dashboard?go-live=1.
   ═══════════════════════════════════════════════════════════════════ */

const CREATE_MODES = [
  { value: "photo", label: "PHOTO" },
  { value: "video", label: "VIDEO" },
  { value: "story", label: "STORY" },
  { value: "live",  label: "LIVE"  },
];

export function SocialCreate() {
  const { user } = useAuth();
  const nav = useNavigate();

  // Mode state — mode "video" is the TikTok default. URL param ?type=story
  // pre-selects the story tab.
  const initialMode = (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("type")) || "video";
  const [mode, setMode] = useState(["photo", "video", "story", "live"].includes(initialMode) ? initialMode : "video");

  // Camera + recording
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const [facing, setFacing] = useState("user");
  const [camReady, setCamReady] = useState(false);
  const [camError, setCamError] = useState(null);
  const [recording, setRecording] = useState(false);
  const [progress, setProgress] = useState(0); // seconds
  const maxSec = mode === "story" ? 15 : 60;

  // Captured media
  const [captured, setCaptured] = useState(null); // { type:'photo'|'video', file, preview }

  // Music
  const [musicOpen, setMusicOpen] = useState(false);
  const [music, setMusic] = useState(null);
  const audioRef = useRef(null);

  // Caption + busy
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  /* ── Camera lifecycle ──────────────────────────────────────────── */
  const startCamera = async (face = facing) => {
    try {
      // Stop any previous stream
      if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
      const constraints = { video: { facingMode: face, width: { ideal: 1080 }, height: { ideal: 1920 } }, audio: true };
      const s = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = s;
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        try { await videoRef.current.play(); } catch (_) {}
      }
      setCamReady(true);
      setCamError(null);
    } catch (err) {
      setCamReady(false);
      setCamError("Camera unavailable — pick from your gallery instead.");
    }
  };

  // Start camera on mount + whenever the user flips (but skip when LIVE
  // mode — that route hands off to the broadcaster).
  useEffect(() => {
    if (mode === "live") {
      // Tear down camera so the LIVE preview can claim it.
      if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
      setCamReady(false);
      return;
    }
    if (captured) return; // hold preview, don't reopen camera
    startCamera(facing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facing, mode, captured]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    if (recorderRef.current && recorderRef.current.state === "recording") recorderRef.current.stop();
    if (tickRef.current) cancelAnimationFrame(tickRef.current);
  }, []);

  // Auto-play music preview when a track is selected
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      if (music?.url) {
        audioRef.current.src = music.url;
        audioRef.current.loop = true;
        audioRef.current.volume = 0.55;
        audioRef.current.play().catch(() => {});
      }
    }
  }, [music]);

  /* ── Capture helpers ───────────────────────────────────────────── */
  const flipCamera = () => setFacing((f) => (f === "user" ? "environment" : "user"));

  const snapPhoto = () => {
    const v = videoRef.current; const c = canvasRef.current;
    if (!v || !c || !camReady) { toast.error("Camera not ready"); return; }
    const w = v.videoWidth || 720;
    const h = v.videoHeight || 1280;
    c.width = w; c.height = h;
    const ctx = c.getContext("2d");
    if (facing === "user") { ctx.translate(w, 0); ctx.scale(-1, 1); }
    ctx.drawImage(v, 0, 0, w, h);
    c.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `photo-${Date.now()}.jpg`, { type: "image/jpeg" });
      setCaptured({ type: "photo", file, preview: URL.createObjectURL(blob) });
    }, "image/jpeg", 0.92);
  };

  const startRec = () => {
    if (!streamRef.current) { toast.error("Camera not ready"); return; }
    try {
      const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
        ? "video/webm;codecs=vp9,opus"
        : "video/webm";
      const rec = new MediaRecorder(streamRef.current, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        const file = new File([blob], `${mode}-${Date.now()}.webm`, { type: "video/webm" });
        setCaptured({ type: "video", file, preview: URL.createObjectURL(blob) });
      };
      recorderRef.current = rec;
      rec.start();
      const startedAt = Date.now();
      setRecording(true); setProgress(0);
      const tick = () => {
        const elapsed = (Date.now() - startedAt) / 1000;
        setProgress(Math.min(maxSec, elapsed));
        if (recorderRef.current?.state === "recording" && elapsed < maxSec) {
          tickRef.current = requestAnimationFrame(tick);
        } else if (recorderRef.current?.state === "recording") {
          stopRec();
        }
      };
      tickRef.current = requestAnimationFrame(tick);
    } catch (e) { toast.error("Recording not supported on this browser"); }
  };

  const stopRec = () => {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
    if (tickRef.current) cancelAnimationFrame(tickRef.current);
    setRecording(false);
  };

  const onGalleryPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const type = f.type.startsWith("video") ? "video" : "photo";
    setCaptured({ type, file: f, preview: URL.createObjectURL(f) });
  };

  const reset = () => {
    setCaptured(null);
    setProgress(0);
    setCaption("");
  };

  /* ── Submit pipeline ───────────────────────────────────────────── */
  const submit = async () => {
    if (!user) { toast.error("Sign in to share"); nav("/login?next=/social/create"); return; }
    if (!captured) { toast.error("Capture or pick a clip first"); return; }
    setBusy(true);
    try {
      // 1) Upload media
      const fd = new FormData();
      fd.append("file", captured.file);
      const endpoint = captured.type === "video" ? "/uploads/video" : "/uploads/image";
      const up = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const storage = up.data?.storage_path;
      if (!storage) throw new Error("Upload failed");
      const apiBase = api.defaults.baseURL || "";
      const cleanUrl = `${apiBase.replace(/\/+$/, "")}/files/${storage}`;

      // 2) Create the right kind of object
      if (mode === "story") {
        await api.post("/community/stories", {
          video_url: cleanUrl,
          title: caption.trim().slice(0, 140),
          duration_sec: Math.round(progress) || 8,
          music: music ? { id: music.id, title: music.title, artist: music.artist, preview_url: music.url } : null,
        });
        // bump music usage
        if (music?.id) api.post(`/social/music/${music.id}/use`).catch(() => {});
        toast.success("Story posted!");
        nav("/social");
      } else {
        await api.post("/community/posts", {
          title: caption.trim().slice(0, 140) || (mode === "photo" ? "📸 New photo" : "🎬 New clip"),
          content: caption.trim(),
          image_url: captured.type === "photo" ? cleanUrl : "",
          video_url: captured.type === "video" ? cleanUrl : null,
          music: music ? { id: music.id, title: music.title, artist: music.artist, preview_url: music.url } : null,
          tags: [mode],
        });
        if (music?.id) api.post(`/social/music/${music.id}/use`).catch(() => {});
        toast.success("Shared to your feed!");
        nav("/social");
      }
    } catch (e) {
      const msg = formatApiError(e);
      // Friendly fallback for non-creators trying to upload video
      if (captured.type === "video" && /creator/i.test(msg)) {
        toast.error("Video uploads are creator-only. Apply to become a creator!");
        nav("/apply-creator");
      } else {
        toast.error(msg);
      }
    }
    finally { setBusy(false); }
  };

  /* ── Render ────────────────────────────────────────────────────── */
  // LIVE mode is a hand-off card, no camera here — go-live happens on /dashboard.
  if (mode === "live") {
    return (
      <div data-testid="social-create" className="bg-black min-h-[calc(100vh-120px)] flex flex-col">
        <CreateHeader onClose={() => nav("/social")} mode={mode} setMode={setMode} />
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-5">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-red-500 via-rose-500 to-amber-400 flex items-center justify-center shadow-[0_0_60px_rgba(239,68,68,0.5)] animate-pulse">
            <Radio className="w-12 h-12 text-white" />
          </div>
          <div>
            <div className="text-white font-display font-black text-3xl tracking-tight">GO LIVE</div>
            <div className="text-white/70 text-sm mt-1 max-w-xs mx-auto">Broadcast in real time. Add co-hosts, take tips, and turn viewers into superfans.</div>
          </div>
          <button
            data-testid="create-go-live-btn"
            onClick={() => nav("/studio/live")}
            className="bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-400 hover:to-rose-500 text-white font-black text-lg px-8 py-3.5 rounded-full inline-flex items-center gap-2 shadow-2xl"
          >
            <Radio className="w-5 h-5" /> Start broadcasting
          </button>
          <Link to="/studio/console" className="text-white/50 hover:text-white text-xs underline">…or open the Full Studio (advanced)</Link>
        </div>
        {/* Mode tabs — always visible so the user can return to camera modes */}
        <div className="bg-gradient-to-t from-black via-black/95 to-transparent pb-5 pt-4 px-4">
          <div className="flex items-center justify-center gap-1">
            {CREATE_MODES.map((m) => (
              <button
                key={m.value}
                data-testid={`create-mode-${m.value}`}
                onClick={() => setMode(m.value)}
                className={`uppercase text-[11px] tracking-[0.18em] font-black px-3 py-1.5 rounded-full transition ${mode === m.value ? "text-white" : "text-white/45 hover:text-white/70"}`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="social-create" className="bg-black min-h-[calc(100vh-120px)] flex flex-col relative overflow-hidden">
      <CreateHeader onClose={() => nav("/social")} mode={mode} setMode={setMode} />

      {/* Camera / preview viewport (TikTok 9:16) */}
      <div className="relative flex-1 flex items-center justify-center bg-black">
        {/* Live camera preview */}
        {!captured && (
          <video
            ref={videoRef}
            data-testid="create-camera-preview"
            playsInline
            muted
            autoPlay
            className={`w-full h-full object-cover absolute inset-0 ${facing === "user" ? "scale-x-[-1]" : ""}`}
          />
        )}
        {/* Captured preview */}
        {captured && captured.type === "photo" && (
          <img src={captured.preview} alt="" data-testid="create-photo-preview" className="absolute inset-0 w-full h-full object-cover" />
        )}
        {captured && captured.type === "video" && (
          <video src={captured.preview} data-testid="create-video-preview" playsInline autoPlay loop muted={!!music} className="absolute inset-0 w-full h-full object-cover" />
        )}

        {/* Hidden capture canvas */}
        <canvas ref={canvasRef} className="hidden" />
        {/* Hidden background music for the captured clip */}
        <audio ref={audioRef} className="hidden" />

        {/* Camera unavailable */}
        {!captured && camError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-black via-rose-950 to-black gap-3 px-6 text-center">
            <Camera className="w-10 h-10 text-rose-300" />
            <div className="text-white text-sm">{camError}</div>
            <button data-testid="create-open-gallery" onClick={() => fileRef.current?.click()} className="bg-white/10 hover:bg-white/20 text-white font-bold text-sm px-4 py-2 rounded-full inline-flex items-center gap-1.5">
              <ImagePlus className="w-4 h-4" /> Pick from gallery
            </button>
          </div>
        )}

        {/* Music chip (top center) */}
        {!captured && (
          <button
            data-testid="create-music-btn"
            onClick={() => setMusicOpen(true)}
            className="absolute top-3 left-1/2 -translate-x-1/2 bg-black/70 backdrop-blur-md border border-white/20 text-white text-xs font-bold px-4 py-1.5 rounded-full inline-flex items-center gap-2 hover:bg-black/90"
          >
            <Music2 className="w-3.5 h-3.5" />
            <span className="truncate max-w-[150px]">{music ? `${music.title} · ${music.artist}` : "Add music"}</span>
          </button>
        )}

        {/* Right-rail tools (only on live preview) */}
        {!captured && (
          <div className="absolute right-3 top-1/3 -translate-y-1/2 flex flex-col gap-3">
            <button data-testid="create-flip-camera" onClick={flipCamera} className="w-10 h-10 rounded-full bg-black/60 backdrop-blur-md border border-white/15 flex items-center justify-center text-white hover:bg-black/80">
              <RotateCcw className="w-5 h-5" />
            </button>
            <button data-testid="create-mute-music" onClick={() => setMusic(null)} disabled={!music} className="w-10 h-10 rounded-full bg-black/60 backdrop-blur-md border border-white/15 flex items-center justify-center text-white hover:bg-black/80 disabled:opacity-40">
              <Music2 className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Recording progress ring (top of record button) */}
        {recording && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 bg-red-600 text-white text-xs font-black px-3 py-1 rounded-full inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> REC · {progress.toFixed(1)}s
          </div>
        )}
      </div>

      {/* Bottom bar */}
      {!captured ? (
        <div className="bg-gradient-to-t from-black via-black/95 to-transparent pt-4 pb-5 px-4">
          {/* Mode tabs */}
          <div className="flex items-center justify-center gap-1 mb-4">
            {CREATE_MODES.map((m) => (
              <button
                key={m.value}
                data-testid={`create-mode-${m.value}`}
                onClick={() => { setMode(m.value); setProgress(0); }}
                className={`uppercase text-[11px] tracking-[0.18em] font-black px-3 py-1.5 rounded-full transition ${mode === m.value ? "text-white" : "text-white/45 hover:text-white/70"}`}
              >
                {m.label}
              </button>
            ))}
          </div>

          {/* Action row: gallery · record · flip */}
          <div className="grid grid-cols-3 items-center">
            <div className="flex justify-start">
              <input ref={fileRef} type="file" accept="image/*,video/*" onChange={onGalleryPick} className="hidden" data-testid="create-file-input" />
              <button data-testid="create-gallery-btn" onClick={() => fileRef.current?.click()} className="w-12 h-12 rounded-2xl bg-white/10 hover:bg-white/20 flex items-center justify-center border border-white/15">
                <ImagePlus className="w-6 h-6 text-white" />
              </button>
            </div>

            {/* Big record / shutter button */}
            <div className="flex justify-center">
              {mode === "photo" ? (
                <button
                  data-testid="create-shutter-btn"
                  onClick={snapPhoto}
                  disabled={!camReady}
                  className="w-20 h-20 rounded-full bg-white ring-4 ring-white/35 hover:scale-105 transition disabled:opacity-50 flex items-center justify-center"
                >
                  <span className="w-16 h-16 rounded-full bg-white border-2 border-black/15" />
                </button>
              ) : (
                <button
                  data-testid="create-record-btn"
                  onPointerDown={startRec}
                  onPointerUp={stopRec}
                  onPointerLeave={() => recording && stopRec()}
                  onTouchStart={(e) => { e.preventDefault(); startRec(); }}
                  onTouchEnd={(e) => { e.preventDefault(); stopRec(); }}
                  disabled={!camReady}
                  className={`relative w-20 h-20 rounded-full transition disabled:opacity-50 ${recording ? "scale-110" : ""}`}
                >
                  {/* Outer ring */}
                  <span className={`absolute inset-0 rounded-full ring-4 ${recording ? "ring-red-500" : "ring-white/35"}`} />
                  {/* Inner red dot */}
                  <span className={`absolute inset-3 rounded-full ${recording ? "bg-red-600 scale-50 rounded-md" : "bg-red-500"} transition-all`} />
                  {/* Progress arc */}
                  {recording && (
                    <svg className="absolute inset-0 -rotate-90" viewBox="0 0 80 80">
                      <circle cx="40" cy="40" r="36" stroke="rgba(239,68,68,0.25)" strokeWidth="4" fill="none" />
                      <circle cx="40" cy="40" r="36" stroke="#ef4444" strokeWidth="4" fill="none" strokeDasharray={`${(progress / maxSec) * 226} 226`} strokeLinecap="round" />
                    </svg>
                  )}
                </button>
              )}
            </div>

            <div className="flex justify-end">
              <button data-testid="create-flip-camera-bottom" onClick={flipCamera} className="w-12 h-12 rounded-2xl bg-white/10 hover:bg-white/20 flex items-center justify-center border border-white/15">
                <RotateCcw className="w-6 h-6 text-white" />
              </button>
            </div>
          </div>

          <div className="text-center text-white/45 text-[10px] uppercase tracking-[0.25em] mt-3">
            {mode === "photo" ? "Tap to capture" : `Hold to record · up to ${maxSec}s`}
          </div>
        </div>
      ) : (
        /* ── Caption + share screen ─────────────────────────── */
        <div className="bg-gradient-to-t from-black via-black/95 to-transparent pt-4 pb-5 px-4 space-y-3">
          <div className="flex items-center gap-2">
            <button data-testid="create-retake" onClick={reset} className="bg-white/10 hover:bg-white/20 text-white font-bold text-sm px-4 py-2 rounded-full inline-flex items-center gap-1.5">
              <X className="w-4 h-4" /> Retake
            </button>
            {music && (
              <span data-testid="create-music-chip" className="bg-white/10 border border-white/15 text-white text-xs font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 truncate max-w-[180px]">
                <Music2 className="w-3.5 h-3.5" /> <span className="truncate">{music.title}</span>
              </span>
            )}
          </div>
          <textarea
            data-testid="create-caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value.slice(0, 1000))}
            placeholder="Add a caption…  #fyp"
            rows={2}
            className="w-full bg-white/5 border border-white/15 rounded-2xl px-4 py-3 text-sm text-white placeholder-white/40 outline-none resize-none focus:border-red-400"
          />
          <button
            data-testid="create-submit"
            onClick={submit}
            disabled={busy}
            className="w-full bg-gradient-to-r from-red-500 via-rose-500 to-amber-500 hover:from-red-400 text-white font-black px-5 py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
          ><Send className="w-4 h-4" /> {busy ? "Sharing…" : (mode === "story" ? "Share to story" : "Share to feed")}</button>
        </div>
      )}

      {/* Music bottom-sheet */}
      {musicOpen && (
        <MusicSheet
          onClose={() => setMusicOpen(false)}
          onPick={(t) => { setMusic(t); setMusicOpen(false); }}
          current={music}
        />
      )}
    </div>
  );
}

/* ── Header ──────────────────────────────────────────────────────── */
function CreateHeader({ onClose, mode, setMode }) {
  return (
    <header className="px-4 pt-3 pb-2 flex items-center justify-between bg-black z-10">
      <button data-testid="create-close" onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
        <X className="w-5 h-5 text-white" />
      </button>
      <div className="text-white font-black text-sm uppercase tracking-[0.25em]">{mode === "live" ? "Go Live" : "Create"}</div>
      {mode !== "live" ? (
        <button
          data-testid="create-jump-live"
          onClick={() => setMode("live")}
          className="bg-red-500 hover:bg-red-600 text-white font-black text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full inline-flex items-center gap-1 shadow-lg"
        >
          <Radio className="w-3.5 h-3.5" /> Live
        </button>
      ) : (
        <span className="w-9 h-9" />
      )}
    </header>
  );
}


/* Lightweight stub pages for the other tabs — they hand off to the
 * existing endpoints but render in the Social shell. */
export function SocialExploreV1() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/community/explore?limit=60").then((r) => setItems(r.data?.items || r.data?.posts || [])).catch(() => {}); }, []);
  return (
    <div data-testid="social-explore" className="grid grid-cols-3 gap-0.5 sm:gap-1 px-0">
      {items.map((it) => (
        <Link key={it.id} to={`/social/p/${it.id}`} className="aspect-square bg-black overflow-hidden">
          {it.thumbnail_url || it.media_url
            ? <img src={it.thumbnail_url || it.media_url} alt="" className="w-full h-full object-cover hover:scale-105 transition" />
            : <div className="w-full h-full bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center text-2xl">✨</div>}
        </Link>
      ))}
      {items.length === 0 && <div data-testid="explore-empty" className="col-span-3 text-center text-white/50 py-16">Discover the latest from the community</div>}
    </div>
  );
}

export function SocialReels() {
  const [items, setItems] = useState([]);
  const [i, setI] = useState(0);
  useEffect(() => { api.get("/shorts?limit=30").then((r) => setItems(r.data || [])).catch(() => {}); }, []);
  const v = items[i];
  return (
    <div data-testid="social-reels" className="bg-black h-[calc(100vh-120px)] -mx-0 sm:rounded-2xl overflow-hidden flex items-center justify-center relative">
      {v
        ? (v.embed_url || v.video_url || v.hls_url)
          ? <video key={v.id} src={v.video_url || v.hls_url} autoPlay loop playsInline controls className="w-full h-full object-contain" />
          : <div className="text-white/60 text-center">Loading reel…</div>
        : <div className="text-white/60 text-center" data-testid="reels-empty">No reels yet</div>}
      <div className="absolute right-3 bottom-20 sm:bottom-6 flex flex-col gap-2">
        <button onClick={() => setI((n) => Math.max(0, n - 1))} className="w-12 h-12 rounded-full bg-white/15 hover:bg-white/30 text-white" aria-label="Previous">↑</button>
        <button onClick={() => setI((n) => Math.min(items.length - 1, n + 1))} className="w-12 h-12 rounded-full bg-white/15 hover:bg-white/30 text-white" aria-label="Next">↓</button>
      </div>
      {v && (
        <div className="absolute left-3 bottom-3 right-20 text-white">
          <div className="font-display font-black text-lg drop-shadow">{v.title}</div>
          <Link to={`/social/u/${v.creator_id}`} className="text-sm hover:underline">@{v.handle || v.creator_name}</Link>
        </div>
      )}
    </div>
  );
}

export function SocialLive() {
  const [streams, setStreams] = useState([]);
  useEffect(() => { api.get("/live").then((r) => setStreams(r.data || [])).catch(() => {}); }, []);
  return (
    <div data-testid="social-live" className="px-3 sm:px-0 space-y-3">
      <div className="text-center py-2 inline-flex items-center gap-2 bg-red-600/20 text-red-300 font-black text-xs uppercase tracking-[0.25em] px-3 py-1.5 rounded-full mx-auto justify-center w-fit">
        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> LIVE NOW
      </div>
      {streams.length === 0 && <div data-testid="live-empty" className="text-white/50 text-center py-12">No one's live right now — check back soon!</div>}
      {streams.map((s) => (
        <Link key={s.id} to={`/live/${s.id}`} data-testid={`social-live-${s.id}`} className="block rounded-2xl overflow-hidden bg-black/40 border border-white/10 hover:border-red-500/50 transition">
          <div className="relative aspect-video bg-black">
            {s.thumbnail_url ? <img src={s.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-fuchsia-500 to-rose-500 flex items-center justify-center text-5xl">📡</div>}
            <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-red-600 text-white font-black text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full animate-pulse"><span className="w-1.5 h-1.5 rounded-full bg-white" /> LIVE</span>
          </div>
          <div className="p-3">
            <div className="font-bold">{s.title}</div>
            <div className="text-white/60 text-xs">@{s.handle || s.creator_name}</div>
          </div>
        </Link>
      ))}
    </div>
  );
}

export function SocialMeV1() {
  const { user } = useAuth();
  if (!user) return (
    <div className="text-center py-12 px-4">
      <div className="text-4xl mb-2">👋</div>
      <div className="font-bold mb-3">Sign in to view your profile</div>
      <Link to="/login" className="bg-gradient-to-r from-pink-500 to-fuchsia-500 text-white font-black px-5 py-2.5 rounded-full">Sign in</Link>
    </div>
  );
  return (
    <div data-testid="social-me" className="px-3 sm:px-0 space-y-4">
      <div className="rounded-2xl bg-gradient-to-br from-pink-500 via-fuchsia-500 to-purple-600 p-6 text-center">
        <div className="w-24 h-24 rounded-full mx-auto bg-white/25 ring-4 ring-white/40 overflow-hidden">
          {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-4xl">⭐</div>}
        </div>
        <div className="mt-3 font-display font-black text-2xl tracking-tight">{user.name}</div>
        <div className="text-white/80 text-sm">@{user.handle || user.email?.split("@")[0]}</div>
        <div className="mt-3 flex items-center justify-center gap-2">
          <Link to="/profile/edit" className="bg-white/95 text-[#222] font-bold text-sm px-4 py-1.5 rounded-full">Edit profile</Link>
          <Link to={`/timeline/${user.user_id}`} className="bg-white/15 text-white font-bold text-sm px-4 py-1.5 rounded-full">View timeline</Link>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════
   The new tab-aware components (Feed/Explore tabs + Notifications + DMs)
   replace the older single-purpose components without breaking existing
   imports. We re-export them at the bottom.
   ═══════════════════════════════════════════════════════════════════ */

function TabBar({ tabs, value, onChange, testid }) {
  return (
    <div data-testid={testid} className="flex items-center justify-center gap-1 px-3 sm:px-0 py-2">
      {tabs.map((t) => (
        <button
          key={t.value}
          data-testid={`${testid}-${t.value}`}
          onClick={() => onChange(t.value)}
          className={`px-4 py-1.5 rounded-full text-sm font-bold transition ${value === t.value ? "bg-gradient-to-r from-red-500 to-rose-600 text-white shadow-lg" : "text-white/60 hover:text-white"}`}
        >{t.label}</button>
      ))}
    </div>
  );
}

/* ── Feed with Following | Explore | For You tabs ─────────────────── */
export function SocialFeedTabbed() {
  const [tab, setTab] = useState("foryou");
  const [stories, setStories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [liveNow, setLiveNow] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const endpoint =
      tab === "following" ? "/community/feed?scope=following&limit=40" :
      tab === "explore"   ? "/community/explore?limit=40"             :
                            "/community/feed?limit=40";
    Promise.all([
      api.get("/community/stories?limit=24").then((r) => r.data?.stories || []).catch(() => []),
      api.get(endpoint).then((r) => r.data?.posts || r.data?.items || []).catch(() => []),
      api.get("/live?limit=10").then((r) => r.data || []).catch(() => []),
    ]).then(([s, p, l]) => { setStories(s); setPosts(p); setLiveNow(l); setLoading(false); });
  }, [tab]);

  return (
    <div data-testid="social-feed" className="space-y-2">
      <TabBar
        testid="feed-tabs"
        value={tab}
        onChange={setTab}
        tabs={[
          { value: "following", label: "Following" },
          { value: "explore",   label: "Explore" },
          { value: "foryou",    label: "For You" },
        ]}
      />
      {/* LIVE NOW rail — pulled from the existing /api/live endpoint so any
          Tap IN TV creator broadcasting auto-appears here. */}
      {liveNow.length > 0 && (
        <section data-testid="feed-live-now" className="px-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-red-400 font-black mb-2 inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> LIVE NOW
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar">
            {liveNow.map((s) => (
              <Link key={s.id} to={`/live/${s.id}`} data-testid={`feed-live-${s.id}`} className="shrink-0 w-32 sm:w-40">
                <div className="aspect-video rounded-lg overflow-hidden bg-black ring-2 ring-red-500/50 relative">
                  {s.thumbnail_url
                    ? <img src={s.thumbnail_url} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full bg-gradient-to-br from-red-500 to-rose-700 flex items-center justify-center text-3xl">📡</div>}
                  <span className="absolute top-1 left-1 bg-red-600 text-white text-[9px] font-black uppercase px-1.5 py-0.5 rounded animate-pulse">LIVE</span>
                </div>
                <div className="text-xs font-bold mt-1 truncate">{s.title}</div>
                <div className="text-[10px] text-white/50 truncate">@{s.handle || s.creator_name}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Stories rail */}
      <section data-testid="social-stories" className="px-3 pt-1">
        <div className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar">
          <Link to="/social/create?type=story" data-testid="story-add" className="shrink-0 w-16 flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-500 via-rose-500 to-amber-400 p-[3px]">
              <div className="w-full h-full rounded-full bg-black flex items-center justify-center text-2xl">+</div>
            </div>
            <div className="text-[10px] mt-1 truncate">Your story</div>
          </Link>
          {stories.map((s) => (
            <Link key={s.id} to={`/social/story/${s.id}`} data-testid={`story-${s.id}`} className="shrink-0 w-16 flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-500 via-rose-500 to-amber-400 p-[3px]">
                <img src={s.media_url || s.thumbnail_url || s.user?.picture} alt="" className="w-full h-full object-cover rounded-full ring-2 ring-black" />
              </div>
              <div className="text-[10px] mt-1 truncate w-full">{s.user?.handle || s.user?.name}</div>
            </Link>
          ))}
        </div>
      </section>

      {loading && <div className="text-center text-white/50 py-12">Loading…</div>}
      {!loading && posts.length === 0 && (
        <div data-testid="social-feed-empty" className="bg-white/[0.03] border-y border-white/10 p-8 text-center">
          <Sparkles className="w-8 h-8 text-red-400 mx-auto mb-2" />
          <div className="font-bold">It's quiet here…</div>
          <div className="text-white/60 text-sm mt-1">Be the first to post — tap the + button.</div>
        </div>
      )}
      <div className="space-y-0 divide-y divide-white/10">
        {posts.map((p) => <PostCard key={p.id} post={p} />)}
      </div>
    </div>
  );
}

/* ── Explore with Top | People | Videos | Live tabs ───────────────── */
export function SocialExploreTabbed() {
  const [tab, setTab] = useState("top");
  const [items, setItems] = useState([]);
  const [people, setPeople] = useState([]);
  const [videos, setVideos] = useState([]);
  const [streams, setStreams] = useState([]);

  useEffect(() => {
    if (tab === "top")    api.get("/community/explore?limit=60").then((r) => setItems(r.data?.items || r.data?.posts || [])).catch(() => {});
    if (tab === "people") api.get("/users/discover?limit=40").then((r) => setPeople(r.data?.users || r.data || [])).catch(() => setPeople([]));
    if (tab === "videos") api.get("/shorts?limit=60").then((r) => setVideos(r.data || [])).catch(() => {});
    if (tab === "live")   api.get("/live").then((r) => setStreams(r.data || [])).catch(() => {});
  }, [tab]);

  return (
    <div data-testid="social-explore" className="space-y-3">
      <TabBar
        testid="explore-tabs"
        value={tab}
        onChange={setTab}
        tabs={[
          { value: "top",    label: "Top" },
          { value: "people", label: "People" },
          { value: "videos", label: "Videos" },
          { value: "live",   label: "Live" },
        ]}
      />
      {tab === "top" && (
        <div className="grid grid-cols-3 gap-0.5 sm:gap-1 px-0">
          {items.map((it) => (
            <Link key={it.id} to={`/social/p/${it.id}`} className="aspect-square bg-black overflow-hidden">
              {it.thumbnail_url || it.media_url
                ? <img src={it.thumbnail_url || it.media_url} alt="" className="w-full h-full object-cover hover:scale-105 transition" />
                : <div className="w-full h-full bg-gradient-to-br from-red-500 to-rose-700 flex items-center justify-center text-2xl">✨</div>}
            </Link>
          ))}
          {items.length === 0 && <div data-testid="explore-empty" className="col-span-3 text-center text-white/50 py-16">Discover the latest from the community</div>}
        </div>
      )}
      {tab === "people" && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 px-3 sm:px-0">
          {people.map((u) => (
            <Link key={u.user_id} to={`/social/u/${u.user_id}`} data-testid={`people-${u.user_id}`} className="rounded-2xl bg-black/30 border border-white/10 p-4 text-center hover:bg-white/5 transition">
              <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-red-500 to-rose-600 ring-2 ring-white/20 overflow-hidden">
                {u.picture ? <img src={u.picture} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-xl">👤</div>}
              </div>
              <div className="font-bold mt-2 truncate text-sm">{u.name || u.handle}</div>
              <div className="text-[10px] text-white/50 truncate">@{u.handle || u.email?.split("@")[0]}</div>
            </Link>
          ))}
          {people.length === 0 && <div className="col-span-full text-center text-white/50 py-16">No people to discover yet</div>}
        </div>
      )}
      {tab === "videos" && (
        <div className="grid grid-cols-3 gap-0.5 sm:gap-1 px-0">
          {videos.map((v) => (
            <Link key={v.id} to={`/watch/${v.id}`} className="aspect-square bg-black overflow-hidden relative">
              {v.thumbnail_url
                ? <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full bg-gradient-to-br from-red-500 to-rose-700 flex items-center justify-center text-2xl">🎬</div>}
              <span className="absolute bottom-1 right-1 bg-black/70 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">▶</span>
            </Link>
          ))}
        </div>
      )}
      {tab === "live" && (
        <div className="px-3 sm:px-0 space-y-3">
          {streams.length === 0 && <div data-testid="live-empty" className="text-white/50 text-center py-12">No one's live right now — check back soon!</div>}
          {streams.map((s) => (
            <Link key={s.id} to={`/live/${s.id}`} data-testid={`explore-live-${s.id}`} className="block rounded-2xl overflow-hidden bg-black/40 border border-white/10 hover:border-red-500/50 transition">
              <div className="relative aspect-video bg-black">
                {s.thumbnail_url ? <img src={s.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-red-500 to-rose-700 flex items-center justify-center text-5xl">📡</div>}
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-red-600 text-white font-black text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full animate-pulse"><span className="w-1.5 h-1.5 rounded-full bg-white" /> LIVE</span>
              </div>
              <div className="p-3">
                <div className="font-bold">{s.title}</div>
                <div className="text-white/60 text-xs">@{s.handle || s.creator_name}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Profile with 3-col grid + stats ──────────────────────────────── */
export function SocialMeRevamp() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ posts: 0, followers: 0, following: 0 });
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get(`/users/${user.user_id}/stats`).then((r) => setStats(r.data || { posts: 0, followers: 0, following: 0 })).catch(() => {});
    api.get(`/community/posts?author=${user.user_id}&limit=60`).then((r) => setPosts(r.data?.posts || r.data || [])).catch(() => setPosts([]));
  }, [user]);

  return (
    <div data-testid="social-me" className="px-3 sm:px-0 space-y-4">
      <div className="rounded-2xl bg-gradient-to-br from-red-500 via-rose-600 to-red-800 p-6 text-center">
        <div className="w-24 h-24 rounded-full mx-auto bg-white/25 ring-4 ring-white/40 overflow-hidden">
          {user?.picture
            ? <img src={user.picture} alt="" className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center text-4xl">⭐</div>}
        </div>
        <div className="mt-3 font-display font-black text-2xl tracking-tight">{user?.name}</div>
        <div className="text-white/80 text-sm">@{user?.handle || user?.email?.split("@")[0]}</div>
        <div className="mt-4 flex items-center justify-center gap-6 text-center">
          <div><div data-testid="me-stat-posts" className="font-display font-black text-xl">{stats.posts}</div><div className="text-[10px] uppercase tracking-wider opacity-80">Posts</div></div>
          <div><div data-testid="me-stat-followers" className="font-display font-black text-xl">{stats.followers}</div><div className="text-[10px] uppercase tracking-wider opacity-80">Followers</div></div>
          <div><div data-testid="me-stat-following" className="font-display font-black text-xl">{stats.following}</div><div className="text-[10px] uppercase tracking-wider opacity-80">Following</div></div>
        </div>
        <div className="mt-3 flex items-center justify-center gap-2">
          <Link to="/profile/edit" data-testid="me-edit-profile" className="bg-white/95 text-[#222] font-bold text-sm px-4 py-1.5 rounded-full">Edit profile</Link>
          <Link to={`/timeline/${user?.user_id}`} className="bg-white/15 text-white font-bold text-sm px-4 py-1.5 rounded-full">View timeline</Link>
        </div>
      </div>
      <div data-testid="me-grid" className="grid grid-cols-3 gap-0.5 sm:gap-1">
        {posts.map((p) => (
          <Link key={p.id} to={`/social/p/${p.id}`} className="aspect-square bg-black overflow-hidden">
            {p.thumbnail_url || p.media_url
              ? <img src={p.thumbnail_url || p.media_url} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full bg-gradient-to-br from-red-500 to-rose-700 flex items-center justify-center text-2xl">📝</div>}
          </Link>
        ))}
        {posts.length === 0 && (
          <div className="col-span-3 text-center text-white/50 py-12">
            No posts yet. <Link to="/social/create" className="text-red-400 underline">Create your first</Link>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Notifications with All | Likes | Comments | Mentions tabs ────── */
export function SocialNotifications() {
  const [tab, setTab] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    let cancelled = false;
    // Safety: if the fetch hangs or the network is dead, flip out of
    // 'Loading…' after 6s so the user sees the empty state.
    const t = setTimeout(() => { if (!cancelled) setLoading(false); }, 6000);
    api.get("/notifications/mine?limit=80")
      .then((r) => { if (!cancelled) setItems(r.data?.notifications || r.data || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); clearTimeout(t); });
    return () => { cancelled = true; clearTimeout(t); };
  }, []);

  // Mark read on mount
  useEffect(() => { api.post("/notifications/mark-read").catch(() => {}); }, []);

  const filtered = items.filter((n) => {
    if (tab === "all") return true;
    if (tab === "likes")    return (n.type || "").includes("like");
    if (tab === "comments") return (n.type || "").includes("comment");
    if (tab === "mentions") return (n.type || "").includes("mention");
    return true;
  });

  return (
    <div data-testid="social-notifications" className="space-y-2">
      <TabBar
        testid="notif-tabs"
        value={tab}
        onChange={setTab}
        tabs={[
          { value: "all",      label: "All" },
          { value: "likes",    label: "Likes" },
          { value: "comments", label: "Comments" },
          { value: "mentions", label: "Mentions" },
        ]}
      />
      {loading && <div className="text-center text-white/50 py-12">Loading…</div>}
      {!loading && filtered.length === 0 && (
        <div data-testid="notif-empty" className="text-center text-white/50 py-16">
          <Sparkles className="w-10 h-10 mx-auto opacity-40 mb-2" />
          You're all caught up.
        </div>
      )}
      <ul className="divide-y divide-white/5">
        {filtered.map((n) => (
          <li key={n.id} data-testid={`notif-${n.id}`} className="flex items-center gap-3 px-3 sm:px-4 py-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-rose-600 ring-2 ring-white/15 overflow-hidden shrink-0">
              {n.actor?.picture
                ? <img src={n.actor.picture} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-sm font-black">{(n.actor?.name || n.title || "?")[0]}</div>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm leading-snug">
                <span className="font-bold">{n.actor?.name || n.title || "Tap IN"}</span>{" "}
                <span className="text-white/70">{n.body || n.message || ""}</span>
              </div>
              {n.created_at && <div className="text-[10px] text-white/40 mt-0.5">{new Date(n.created_at).toLocaleString()}</div>}
            </div>
            {n.link && (
              <Link to={n.link} className="text-red-400 hover:text-red-300 text-xs font-bold shrink-0">View</Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Messages (DM inbox) ───────────────────────────────────────────── */
export function SocialMessages() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/social/dm/threads")
      .then((r) => setThreads(r.data || []))
      .catch(() => setThreads([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="social-messages" className="space-y-2">
      <header className="px-4 sm:px-3 py-3 flex items-center justify-between">
        <h2 className="font-display font-black text-2xl tracking-tight">Messages</h2>
      </header>
      {loading && <div className="text-center text-white/50 py-12">Loading…</div>}
      {!loading && threads.length === 0 && (
        <div data-testid="dm-empty" className="text-center text-white/50 py-16">
          <MessageCircle className="w-10 h-10 mx-auto opacity-40 mb-2" />
          No conversations yet. Open a creator's profile and tap "Message" to start one.
        </div>
      )}
      <ul className="divide-y divide-white/5">
        {threads.map((t) => (
          <li key={t.id}>
            <Link to={`/social/messages/${t.id}`} data-testid={`dm-thread-${t.id}`} className="flex items-center gap-3 px-3 sm:px-4 py-3 hover:bg-white/5">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-rose-600 ring-2 ring-white/15 overflow-hidden shrink-0">
                {t.peer?.picture
                  ? <img src={t.peer.picture} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center font-black">{(t.peer?.name || "?")[0]}</div>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-bold truncate">{t.peer?.name || t.peer?.handle || "Tap IN user"}</div>
                  {t.last_message_at && <div className="text-[10px] text-white/40 shrink-0">{new Date(t.last_message_at).toLocaleDateString()}</div>}
                </div>
                <div className="text-sm text-white/60 truncate">{t.last_message || "Say hello"}</div>
              </div>
              {t.unread_count > 0 && (
                <span data-testid={`dm-unread-${t.id}`} className="bg-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full">{t.unread_count}</span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── DM thread (conversation) ─────────────────────────────────────── */
export function SocialThread() {
  const { tid } = useReactRouterParams();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const scroller = useRef(null);

  const load = () => {
    api.get(`/social/dm/threads/${tid}/messages?limit=80`)
      .then((r) => setMessages(r.data || []))
      .catch(() => {});
    api.post(`/social/dm/threads/${tid}/read`).catch(() => {});
  };
  useEffect(() => { if (tid) load(); }, [tid]);
  useEffect(() => { scroller.current?.scrollTo(0, scroller.current.scrollHeight); }, [messages]);

  const send = async (e) => {
    e?.preventDefault?.();
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    try {
      await api.post(`/social/dm/threads/${tid}/messages`, { body });
      setText("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="social-thread" className="flex flex-col h-[calc(100vh-120px)]">
      <header className="px-4 sm:px-3 py-3 flex items-center gap-3 border-b border-white/10">
        <Link to="/social/messages" className="text-white/70 hover:text-white" data-testid="thread-back">←</Link>
        <h2 className="font-display font-black text-lg">Conversation</h2>
      </header>
      <div ref={scroller} className="flex-1 overflow-y-auto px-3 sm:px-4 py-3 space-y-2">
        {messages.length === 0 && <div className="text-center text-white/50 py-12">Start the conversation</div>}
        {messages.map((m) => {
          const mine = m.from_user_id === user?.user_id;
          return (
            <div key={m.id} data-testid={`dm-msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-gradient-to-br from-red-500 to-rose-600 text-white" : "bg-white/10 text-white"}`}>
                {m.body}
                <div className="text-[9px] opacity-60 mt-1">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="border-t border-white/10 px-3 sm:px-4 py-2 flex items-center gap-2">
        <input
          data-testid="dm-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type a message…"
          className="flex-1 bg-white/5 border border-white/15 rounded-full px-4 py-2 text-white placeholder-white/30 outline-none focus:border-red-400 text-sm"
        />
        <button data-testid="dm-send" type="submit" disabled={busy || !text.trim()} className="bg-red-500 hover:bg-red-600 text-white font-bold px-4 py-2 rounded-full disabled:opacity-50 inline-flex items-center gap-1">
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}

/* Backwards-compat re-exports. The OLD SocialFeed/SocialExplore/SocialMe
   functions live above as SocialFeedV1/V1/V1 for reference. App.js imports
   `SocialFeed`, `SocialExplore`, `SocialMe` — so we alias the new tabbed
   versions under those names. */
export { SocialFeedTabbed as SocialFeed };
export { SocialExploreTabbed as SocialExplore };
export { SocialMeRevamp as SocialMe };
